import { Box, Paper } from "@mui/material"
import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  WAREHOUSE_SHELVING_LAYOUT_ROUTE_PATH,
  WAREHOUSE_SHELVING_ROUTE_PATH,
} from "routes/app/admin/warehouses/$warehouseId/_layout.shelving"

import { WarehouseShelvingFilterChips } from "../components/WarehouseShelvingFilterChips"
import { WarehouseShelvingFilterDrawer } from "../components/WarehouseShelvingFilterDrawer"
import { WarehouseShelvingSearch } from "../components/WarehouseShelvingSearch"
import WarehouseShelvingTable from "../components/WarehouseShelvingTable/WarehouseShelvingTable"
import { useWarehouseShelvingColumns } from "../hooks"
import { useWarehouseShelvingSearchStore } from "../stores/useWarehouseShelvingSearchStore"

import type { WarehouseShelvingSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

const WarehouseShelvingContainer: FC = () => {
  const currentSearch = useSearch({
    from: WAREHOUSE_SHELVING_LAYOUT_ROUTE_PATH,
  })
  const { query, field: sortField, order, ...restSearchParams } = currentSearch
  const navigate = useNavigate()

  const { warehouseId } = useParams({
    from: WAREHOUSE_SHELVING_LAYOUT_ROUTE_PATH,
  })

  const data = useWarehouseShelvingSearchStore(({ data }) => data)
  const fetching = useWarehouseShelvingSearchStore(({ fetching }) => fetching)
  const error = useWarehouseShelvingSearchStore(({ error }) => error)
  const executeSearch = useWarehouseShelvingSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useWarehouseShelvingSearchStore(({ loadMore }) => loadMore)
  const clearStore = useWarehouseShelvingSearchStore(({ clearStore }) => clearStore)
  const refetch = useCallback(
    () => executeSearch(queryClient, { ...currentSearch, warehouseAreaId: warehouseId }, true),
    [executeSearch, currentSearch, warehouseId]
  )

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  useEffect(() => {
    refetch()
  }, [refetch])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  const sorting = useMemo(() => {
    if (!sortField) return []

    const tableSortField = upperSnakeCaseToCamelCase(sortField)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [sortField, order])

  const columns = useWarehouseShelvingColumns(refetch)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const handleSortingChange: OnChangeFn<SortingState> = (updaterFn) => {
    // @ts-expect-error - updaterFn is know fn
    const newSorting = updaterFn(sorting)
    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id
      const sortedField = toUpperSnakeCase(key) as WarehouseShelvingSortField
      const params = {
        ...restSearchParams,
        query,
        field: sortedField,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      const refinedParams = getRefinedSearchParams(params)

      const { field, order, query: newQuery } = refinedParams.search
      navigate({
        search: { field, order, query: newQuery },
        to: WAREHOUSE_SHELVING_ROUTE_PATH,
        params: { warehouseId },
        replace: true,
      })
    }
  }

  const [throttledLoadMore] = useThrottle(() => {
    loadMore(queryClient, { ...currentSearch, warehouseAreaId: warehouseId })
  }, 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const handleFilterDrawerOpen = () => setIsFilterDrawerOpen(true)
  const handleFilterDrawerToggle = useCallback(() => setIsFilterDrawerOpen((prev) => !prev), [])

  return (
    <Paper
      elevation={0}
      className='flex min-h-0 grow flex-col overflow-hidden rounded-lg bg-white'
      data-testid='warehouse-shelving-container'
    >
      <WarehouseShelvingSearch onFilterOpen={handleFilterDrawerOpen} />
      <WarehouseShelvingFilterDrawer showFilter={isFilterDrawerOpen} toggleFilter={handleFilterDrawerToggle} />
      <WarehouseShelvingFilterChips />
      <Box className='flex flex-1 flex-col overflow-y-auto' data-test='warehouse-shelving-table-container'>
        <WarehouseShelvingTable
          data={data}
          fetching={fetching}
          columns={columns}
          sorting={sorting}
          error={error}
          tableContainerRef={tableContainerRef}
          onSortingChange={handleSortingChange}
          onScroll={handleScroll}
        />
      </Box>
    </Paper>
  )
}

export default WarehouseShelvingContainer
